import { Component, OnInit, OnDestroy, NgZone } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { FeatureService } from "../services/features.service";
import { ScreenSizeService } from "../services/screen-size.service";
import { CacheService } from "../services/cache.service";
import { lastValueFrom, map, Subscription } from "rxjs";
import { NetworkService } from "../services/network.service";
import { FeatureUpgradeService } from "../services/feature-upgrade.service";
import { ActiveFeatureService } from "../services/active-feature.service";
import { BackCoordinatorService } from "../services/back-coordinator.service";
import { Capacitor } from "@capacitor/core";

@Component({
  selector: "app-react-native-bundle",
  templateUrl: "./react-native-bundle.component.html",
  styleUrls: ["./react-native-bundle.component.scss"],
})
export class ReactNativeBundleComponent implements OnInit, OnDestroy {
  // Track which features have been loaded to prevent script re-execution
  private static loadedFeatures: Set<string> = new Set();
  // Track persistent containers per module (featureId only, not per route)
  private static persistentContainers: Map<string, HTMLElement> = new Map();
  private static currentActiveFeature: string | null = null;
  // Track running versions of loaded features (baseUrl -> version) - public for upgrade checks
  public static runningVersions: Map<string, string> = new Map();

  // Static method to clear a specific feature from memory
  // Called during feature upgrade to force complete reload
  public static clearFeatureFromMemory(featureId: string): void {
    console.log(`[UPGRADE] Clearing feature ${featureId} from memory...`);

    // Log current state for debugging
    console.log(
      `[UPGRADE] Current persistentContainers keys:`,
      Array.from(ReactNativeBundleComponent.persistentContainers.keys())
    );
    console.log(
      `[UPGRADE] Current loadedFeatures:`,
      Array.from(ReactNativeBundleComponent.loadedFeatures)
    );
    console.log(
      `[UPGRADE] Current active feature:`,
      ReactNativeBundleComponent.currentActiveFeature
    );

    // Remove persistent container from DOM
    if (ReactNativeBundleComponent.persistentContainers.has(featureId)) {
      const container =
        ReactNativeBundleComponent.persistentContainers.get(featureId);
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
        console.log(
          `[UPGRADE] ✅ Removed persistent container for ${featureId}`
        );
      }
      ReactNativeBundleComponent.persistentContainers.delete(featureId);
    }

    // Remove from loaded features set
    if (ReactNativeBundleComponent.loadedFeatures.has(featureId)) {
      ReactNativeBundleComponent.loadedFeatures.delete(featureId);
      console.log(`[UPGRADE] ✅ Removed loaded feature flag for ${featureId}`);
    }

    // Remove script tags for this feature
    const scripts = document.querySelectorAll(
      `script[data-feature-id="${featureId}"]`
    );
    scripts.forEach((script) => {
      const scriptElement = script as HTMLScriptElement;

      // Revoke blob URL if it's a blob
      if (scriptElement.src && scriptElement.src.startsWith("blob:")) {
        URL.revokeObjectURL(scriptElement.src);
      }

      if (scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
    });
    console.log(
      `[UPGRADE] ✅ Removed ${scripts.length} script tags for ${featureId}`
    );

    // Clear current active feature if it matches
    if (ReactNativeBundleComponent.currentActiveFeature === featureId) {
      ReactNativeBundleComponent.currentActiveFeature = null;
      console.log(`[UPGRADE] ✅ Cleared current active feature`);
    }

    console.log(
      `[UPGRADE] ✅ Feature ${featureId} completely cleared from memory`
    );
  }

  // Set the active feature and inform the back coordinator
  private setCurrentActiveFeature(featureId: string | null): void {
    console.log(`[NAV] 📦 [RN-BUNDLE] Setting active feature: ${featureId}`);
    ReactNativeBundleComponent.currentActiveFeature = featureId;
    this.backCoordinator.setActiveFeature(featureId);
  }

  /**
   * Inject a generic navigation capture helper script that lets the shell read
   * the RN navigation back state without modifying any RN feature source.
   */
  private injectNavigationCaptureScript(): void {
    if (document.getElementById("rn-navigation-capture-script")) {
      return;
    }
    const script = document.createElement("script");
    script.id = "rn-navigation-capture-script";
    script.type = "text/javascript";
    script.src = "assets/scripts/rn-navigation-capture.js";
    document.body.appendChild(script);
    console.log("[NAV] 📦 [RN-BUNDLE] Injected navigation capture script");
  }

  /**
   * Patch the RN bundle JS to capture the NavigationContainer ref and expose it
   * to the shell's capture helper. This is a best-effort regex patch that works
   * on unminified webpack bundles; if it fails, the feature can still fall back
   * to the shell-side history handling.
   */
  private patchBundleWithNavigationCapture(jsContent: string): string {
    // Capture the ref and wrap onStateChange so the shell is notified on every
    // React Navigation state change. This is a best-effort regex for unminified
    // webpack bundles.
    const navContainerPattern =
      /NavigationContainer,\s*\{\s*ref:\s*([^,\s]+)\s*,\s*onStateChange:\s*([^,]+)\s*,/g;
    const patched = jsContent.replace(
      navContainerPattern,
      (match, refVar, onStateChange) =>
        `NavigationContainer,{ref:function(ref){${refVar}.current=ref;window.__rnCaptureNavigationRef(ref);},onStateChange:function(state){(${onStateChange})(state);window.__rnReportNavigationState();},`
    );

    if (patched === jsContent) {
      console.warn(
        "[NAV] 📦 [RN-BUNDLE] Could not patch NavigationContainer; bundle may be minified or using a different variable name"
      );
    } else {
      console.log(
        "[NAV] 📦 [RN-BUNDLE] Patched NavigationContainer ref and onStateChange capture into bundle"
      );
    }
    return patched;
  }

  // Static method to clear all cached React Native features and containers
  // Called during logout to ensure clean state for next user
  public static clearAllCachedFeatures(): void {
    console.log("Clearing all cached React Native features...");

    // Remove all persistent containers from the DOM
    ReactNativeBundleComponent.persistentContainers.forEach(
      (container, key) => {
        console.log(`Removing persistent container: ${key}`);
        if (container.parentNode) {
          container.parentNode.removeChild(container);
        }
      }
    );

    // Remove all React Native script tags from the DOM
    // These contain the actual JavaScript bundles loaded into memory
    const scripts = document.querySelectorAll('script[src^="blob:"]');
    scripts.forEach((script) => {
      const scriptElement = script as HTMLScriptElement;
      console.log(`Removing script tag: ${scriptElement.src}`);

      // Revoke the blob URL to free memory
      if (scriptElement.src.startsWith("blob:")) {
        URL.revokeObjectURL(scriptElement.src);
      }

      // Remove the script tag from DOM
      if (scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
    });

    // Clear the maps and sets
    ReactNativeBundleComponent.persistentContainers.clear();
    ReactNativeBundleComponent.loadedFeatures.clear();
    ReactNativeBundleComponent.currentActiveFeature = null;

    console.log("All React Native cached features cleared");
  }

  isMobile: boolean;
  isDesktop: boolean;
  featureId: string;
  baseUrl: string;
  homeName: string;

  networkSubscription: Subscription | null = null;
  mobileSubscription: Subscription | null = null;
  desktopSubscription: Subscription | null = null;
  paramMapSubscription: Subscription | null = null;
  queryParamMapSubscription: Subscription | null = null;
  online = true;

  bundleLoaded = false;

  constructor(
    private route: ActivatedRoute,
    private featureService: FeatureService,
    private screenSizeService: ScreenSizeService,
    private cacheService: CacheService,
    private networkService: NetworkService,
    private featureUpgradeService: FeatureUpgradeService,
    private activeFeatureService: ActiveFeatureService,
    private backCoordinator: BackCoordinatorService,
    private ngZone: NgZone
  ) {
    console.log("Online status: " + this.networkService.isOnline());
    this.online = this.networkService.isOnline();
    this.networkSubscription = this.networkService
      .getOnlineStatus()
      .subscribe((isOnline) => {
        console.log("Network status changed:", isOnline);
        this.online = isOnline;
      });

    this.mobileSubscription = this.screenSizeService.isMobile$.subscribe(
      (isMobile) => {
        this.isMobile = isMobile;
      }
    );

    this.desktopSubscription = this.screenSizeService.isDesktop$.subscribe(
      (isDesktop) => {
        this.isDesktop = isDesktop;
      }
    );
  }

  ngOnInit(): void {
    console.log(
      "🔵 [NEW CODE] React Native Bundle Component ngOnInit - BUILD TIMESTAMP: 2026-02-21 22:18"
    );
    console.log(`📊 [INIT] bundleLoaded state: ${this.bundleLoaded}`);
    console.log(`📊 [INIT] Current route URL: ${this.route.snapshot.url}`);
    console.log(`📊 [INIT] Full router URL: ${window.location.href}`);

    this.resetSafeAreaInsets();

    console.log("React Native: Loading screen");

    // Subscribe to query parameter changes to handle navigation within the same feature
    this.queryParamMapSubscription = this.route.queryParamMap.subscribe(
      (queryParams) => {
        console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
        console.log(`[NAV] 📦 [RN-BUNDLE] queryParamMap subscription fired`);
        console.log(`[NAV] 📦 [RN-BUNDLE] bundleLoaded: ${this.bundleLoaded}`);
        console.log(`[NAV] 📦 [RN-BUNDLE] this.featureId: ${this.featureId}`);

        // Get the current route's featureId from paramMap to avoid race condition
        const routeFeatureId = this.route.snapshot.paramMap.get("featureId");
        console.log(
          `[NAV] 📦 [RN-BUNDLE] route.snapshot.paramMap featureId: ${routeFeatureId}`
        );
        console.log(
          `[NAV] 📦 [RN-BUNDLE] Query params:`,
          queryParams.keys.map((k) => `${k}=${queryParams.get(k)}`)
        );

        // Only dispatch navigation event if:
        // 1. Bundle is already loaded
        // 2. We have a featureId
        // 3. The route's featureId matches this component's featureId (avoid race condition)
        if (
          this.bundleLoaded &&
          this.featureId &&
          routeFeatureId === this.featureId
        ) {
          const navigationParam = queryParams.get("navigationParam");
          const source = queryParams.get("source");
          const timestamp = queryParams.get("timestamp");

          console.log(
            `[NAV] 📦 [RN-BUNDLE] ✅ Route featureId matches, dispatching navigation event`
          );
          console.log(
            `[NAV] 📦 [RN-BUNDLE] navigationParam: ${navigationParam}`
          );
          console.log(`[NAV] 📦 [RN-BUNDLE] source: ${source}`);
          console.log(`[NAV] 📦 [RN-BUNDLE] timestamp: ${timestamp}`);

          // Dispatch navigation event to React Native
          const navigationEvent = new CustomEvent("angularNavigationChange", {
            detail: {
              navigationParam: navigationParam || null,
              featureId: this.featureId,
              source: source,
              timestamp: timestamp,
            },
          });
          window.dispatchEvent(navigationEvent);
          console.log(
            "[NAV] 📦 [RN-BUNDLE] ✅ Dispatched angularNavigationChange event from queryParam change:",
            {
              navigationParam: navigationParam || "default",
              featureId: this.featureId,
              source,
              timestamp,
            }
          );
        } else {
          console.log(
            `[NAV] 📦 [RN-BUNDLE] ⚠️ Skipping navigation event - Race condition or not ready`
          );
          console.log(
            `[NAV] 📦 [RN-BUNDLE] bundleLoaded: ${
              this.bundleLoaded
            }, featureId: ${
              this.featureId
            }, routeFeatureId: ${routeFeatureId}, match: ${
              routeFeatureId === this.featureId
            }`
          );
        }
        console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
      }
    );

    this.paramMapSubscription = this.route.paramMap.subscribe((params) => {
      console.log(`📊 [PARAMS] paramMap subscription fired`);
      console.log(
        `📊 [PARAMS] bundleLoaded at subscription fire: ${this.bundleLoaded}`
      );
      console.log(
        `📊 [PARAMS] All params:`,
        params.keys.map((k) => `${k}=${params.get(k)}`)
      );

      this.featureId = params.get("featureId");
      console.log(`React Native getting feature id: ${this.featureId}`);

      // Always fetch feature description to ensure we have the correct baseUrl
      // sessionStorage can be stale if user navigated to different feature before push notification
      console.log(
        "React Native loading feature description to get correct baseUrl..."
      );
      this.featureService
        .getFeatureDescriptionByBasePath(this.featureId)
        .then(async (rslt) => {
          console.log(
            "🟢 [NEW CODE] INSIDE .then() callback - promise resolved!"
          );
          console.log("React Native got feature descriptions");
          console.log("React Native rslt:", rslt);

          if (!rslt) {
            console.error("React Native: rslt is null/undefined!");
            return;
          }

          sessionStorage.setItem("feature-base-path", rslt.baseUrl);
          this.baseUrl = rslt.baseUrl;
          this.homeName = rslt.homeName;
          this.activeFeatureService.setFeature(rslt.baseUrl, rslt.homeName);

          // Don't set running version here - descriptor may be cached with newer version
          // Running version will be set after bundle actually loads successfully

          const navigationParam =
            this.route.snapshot.queryParamMap.get("navigationParam");
          const uuid = this.route.snapshot.queryParamMap.get("uuid");

          (window as any).initialNavigation = navigationParam;
          (window as any).featureId = uuid;

          const featureUuid = rslt.feature_id;
          console.log(`📊 [LOAD] Feature UUID from descriptor: ${featureUuid}`);
          console.log("React Native loading react native bundle");
          this.loadReactNativeBundle(
            featureUuid,
            navigationParam,
            rslt.offline === "true"
          );

          // Check for upgrade using the baseUrl from the feature description
          this.checkFeatureUpgrade(rslt.baseUrl);
        })
        .catch((error) => {
          console.error(
            "React Native: Error in getFeatureDescriptionByBasePath:",
            error
          );
          console.error("React Native: Error stack:", error?.stack);
        });
    });
  }

  private async checkFeatureUpgrade(baseUrl: string): Promise<void> {
    console.log("[FeatureUpgrade] About to check platform and network status");
    const isNative = Capacitor.isNativePlatform();
    const isOnline = this.networkService.isOnline();
    console.log(
      `[FeatureUpgrade] isNativePlatform: ${isNative}, isOnline: ${isOnline}`
    );
    console.log(`[FeatureUpgrade] Feature baseUrl: ${baseUrl}`);

    if (!isNative || !isOnline) {
      console.log(
        `[FeatureUpgrade] ⚠️  Skipping upgrade check (native: ${isNative}, online: ${isOnline})`
      );
      return;
    }

    console.log(
      `[FeatureUpgrade] 🔍 Checking upgrade for feature (baseUrl: ${baseUrl})`
    );
    console.log(`[FeatureUpgrade] 🟡 Starting async upgrade check...`);

    try {
      console.log(`[FeatureUpgrade] 🟢 About to call checkForFeatureUpgrade()`);
      console.log(
        `[FeatureUpgrade] Starting upgrade check for specific feature only`
      );
      console.log(`[FeatureUpgrade] Calling with baseUrl: "${baseUrl}"`);

      const upgradeCheckStart = Date.now();
      console.log(
        `[FeatureUpgrade] ⏱️  Upgrade check started at ${new Date().toISOString()}`
      );

      const { hasUpgrade, data } =
        await this.featureUpgradeService.checkForFeatureUpgrade(baseUrl);

      const upgradeCheckDuration = Date.now() - upgradeCheckStart;
      console.log(
        `[FeatureUpgrade] ⏱️  Upgrade check completed in ${upgradeCheckDuration}ms`
      );

      if (hasUpgrade && data) {
        console.log(
          `[FeatureUpgrade] ✨ Upgrade available for ${data.featureName}!`
        );
        console.log(
          `[FeatureUpgrade]   📦 Current version: ${data.currentVersion}`
        );
        console.log(`[FeatureUpgrade]   🆕 New version: ${data.newVersion}`);
        console.log(`[FeatureUpgrade]   🔗 Base URL: ${data.baseUrl}`);

        // Show upgrade prompt to user
        const upgraded = await this.featureUpgradeService.promptForUpgrade(
          data
        );

        if (upgraded) {
          console.log(
            `[FeatureUpgrade] User accepted upgrade, reloading feature...`
          );
        } else {
          console.log(`[FeatureUpgrade] User declined upgrade`);
        }
      } else {
        console.log(
          `[FeatureUpgrade] ✅ No upgrade available - feature is up to date`
        );
      }
    } catch (error) {
      console.error(`[FeatureUpgrade] ❌ Error checking for upgrade:`, error);
    }
  }

  ngOnDestroy(): void {
    this.activeFeatureService.clearFeature();

    // Unsubscribe from all subscriptions
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }
    if (this.mobileSubscription) {
      this.mobileSubscription.unsubscribe();
    }
    if (this.desktopSubscription) {
      this.desktopSubscription.unsubscribe();
    }
    if (this.paramMapSubscription) {
      console.log("🔴 [DESTROY] Unsubscribing from paramMap");
      this.paramMapSubscription.unsubscribe();
    }
    if (this.queryParamMapSubscription) {
      console.log("🔴 [DESTROY] Unsubscribing from queryParamMap");
      this.queryParamMapSubscription.unsubscribe();
    }

    // IMPORTANT: Do NOT remove scripts from DOM - they stay loaded permanently
    // This prevents re-execution and multiple React component instances
    // Scripts remain in the DOM for the lifetime of the app session

    // Hide all persistent containers when navigating away
    ReactNativeBundleComponent.persistentContainers.forEach((container) => {
      container.style.display = "none";
    });

    // Reset bundle loaded state
    this.bundleLoaded = false;

    // Clear the active feature so the back coordinator falls back to Angular
    this.setCurrentActiveFeature(null);
  }

  resetSafeAreaInsets() {
    const htmlElement = document.documentElement;
    const innerHeight = window.innerHeight;
    htmlElement.style.setProperty("--client-height", `${innerHeight}px`);
  }

  async loadReactNativeBundle(
    featureId: string,
    navigationParam: string,
    isOffline: boolean
  ): Promise<void> {
    try {
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
      console.log("📦 [RN-BUNDLE] loadReactNativeBundle CALLED");
      console.log("📦 [RN-BUNDLE] featureId:", featureId);
      console.log("📦 [RN-BUNDLE] navigationParam:", navigationParam);
      console.log("📦 [RN-BUNDLE] isOffline:", isOffline);
      console.log("📦 [RN-BUNDLE] this.baseUrl:", this.baseUrl);
      console.log("📦 [RN-BUNDLE] this.bundleLoaded:", this.bundleLoaded);
      console.log(
        "📦 [RN-BUNDLE] persistentContainers.has(featureId):",
        ReactNativeBundleComponent.persistentContainers.has(featureId)
      );
      console.log(
        "📦 [RN-BUNDLE] currentActiveFeature:",
        ReactNativeBundleComponent.currentActiveFeature
      );
      console.log(
        "📦 [RN-BUNDLE] persistentContainers (all):",
        Array.from(ReactNativeBundleComponent.persistentContainers.keys())
      );
      console.log(
        "📦 [RN-BUNDLE] loadedFeatures (all):",
        Array.from(ReactNativeBundleComponent.loadedFeatures)
      );
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

      // Check if this module has already been loaded (cache per module, not per URL)
      if (ReactNativeBundleComponent.persistentContainers.has(featureId)) {
        console.log(
          `📦 [RN-BUNDLE] ✅ Module already loaded: ${featureId} - reattaching container`
        );

        // Hide the currently active feature if different
        if (
          ReactNativeBundleComponent.currentActiveFeature &&
          ReactNativeBundleComponent.currentActiveFeature !== featureId
        ) {
          const oldContainer =
            ReactNativeBundleComponent.persistentContainers.get(
              ReactNativeBundleComponent.currentActiveFeature
            );
          if (oldContainer) {
            console.log(
              `📦 [RN-BUNDLE] Hiding previous feature: ${ReactNativeBundleComponent.currentActiveFeature}`
            );
            console.log(
              `📦 [RN-BUNDLE] Previous container ID: ${oldContainer.id}`
            );
            oldContainer.style.display = "none";
            console.log(`📦 [RN-BUNDLE] ✅ Hidden previous container`);
          }
        }

        // Get the persistent container and reattach it to #react-native-body
        const persistentContainer =
          ReactNativeBundleComponent.persistentContainers.get(featureId);
        if (persistentContainer) {
          console.log(
            `📦 [RN-BUNDLE] Found persistent container: ${persistentContainer.id}`
          );

          const angularContainer = document.getElementById("react-native-body");
          if (angularContainer) {
            console.log(
              `📦 [RN-BUNDLE] Clearing #react-native-body and appending container`
            );
            angularContainer.innerHTML = "";
            angularContainer.appendChild(persistentContainer);
            console.log(
              `📦 [RN-BUNDLE] ✅ Reattached persistent container to #react-native-body`
            );
          }
          persistentContainer.style.display = "block";
          console.log(
            `📦 [RN-BUNDLE] ✅ Showed persistent container: ${persistentContainer.id}`
          );
        }

        // Update current active feature
        this.setCurrentActiveFeature(featureId);
        console.log(
          `📦 [RN-BUNDLE] Updated currentActiveFeature to: ${featureId}`
        );

        // Dispatch navigation event - React Native will handle the route change internally
        console.log(`📦 [RN-BUNDLE] Dispatching angularNavigationChange event`);
        const navigationEvent = new CustomEvent("angularNavigationChange", {
          detail: {
            navigationParam: navigationParam || null,
            featureId,
          },
        });
        window.dispatchEvent(navigationEvent);
        console.log(
          "📦 [RN-BUNDLE] ✅ Dispatched angularNavigationChange event:",
          {
            navigationParam: navigationParam || "default",
            featureId,
          }
        );

        this.bundleLoaded = true;
        console.log(
          "📦 [RN-BUNDLE] ✅ Bundle reactivated (components already mounted)"
        );
        console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
        return;
      }

      console.log(
        `📦 [RN-BUNDLE] 🆕 Loading NEW bundle for feature: ${featureId}`
      );

      // Hide the currently active feature if different
      if (
        ReactNativeBundleComponent.currentActiveFeature &&
        ReactNativeBundleComponent.currentActiveFeature !== featureId
      ) {
        const oldContainer =
          ReactNativeBundleComponent.persistentContainers.get(
            ReactNativeBundleComponent.currentActiveFeature
          );
        if (oldContainer) {
          console.log(
            `📦 [RN-BUNDLE] Hiding previous feature before loading new: ${ReactNativeBundleComponent.currentActiveFeature}`
          );
          console.log(
            `📦 [RN-BUNDLE] Previous container ID: ${oldContainer.id}`
          );
          oldContainer.style.display = "none";
          console.log(`📦 [RN-BUNDLE] ✅ Hidden previous container`);
        }
      }

      // 1. Fetch the HTML
      const indexUrl = `${this.baseUrl}index.html`;
      console.log(`📦 [RN-BUNDLE] Fetching HTML from: ${indexUrl}`);
      const indexResponse = await lastValueFrom(
        this.cacheService
          .getResourceAndOnlyCacheIfNative(indexUrl)
          .pipe(map((response) => response?.data))
      );

      console.log(
        `📦 [RN-BUNDLE] ✅ HTML fetched, length: ${indexResponse?.length || 0}`
      );

      if (!indexResponse) {
        console.error(`📦 [RN-BUNDLE] ❌ Failed to load index.html`);
        throw new Error("Failed to load index.html");
      }

      // Create a persistent container for this module (outside Angular lifecycle)
      let persistentContainer =
        ReactNativeBundleComponent.persistentContainers.get(featureId);
      if (!persistentContainer) {
        console.log(
          `📦 [RN-BUNDLE] Creating NEW persistent container for: ${featureId}`
        );
        persistentContainer = document.createElement("div");
        persistentContainer.id = `rn-persistent-${featureId.replace(
          /[^a-zA-Z0-9]/g,
          "-"
        )}`;
        persistentContainer.className = "react-native-persistent-container";
        persistentContainer.style.cssText =
          "display: flex; flex: 1; flex-direction: column; width: 100%; min-height: 0; overflow: hidden;";
        const rnBody = document.getElementById("react-native-body");
        (rnBody || document.body).appendChild(persistentContainer);
        ReactNativeBundleComponent.persistentContainers.set(
          featureId,
          persistentContainer
        );
        console.log(
          `📦 [RN-BUNDLE] ✅ Created persistent container: ${persistentContainer.id}`
        );
      } else {
        console.log(
          `📦 [RN-BUNDLE] Reusing existing container: ${persistentContainer.id}`
        );
      }

      // Load HTML into the persistent container
      // Strip the full document structure - only inject body content to prevent
      // viewport meta tags from triggering layout recalculation in the WebView
      console.log(`📦 [RN-BUNDLE] Loading HTML into container`);
      const bodyMatch = indexResponse.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      persistentContainer.innerHTML = bodyMatch ? bodyMatch[1] : indexResponse;
      persistentContainer.style.display = "block";
      this.setCurrentActiveFeature(featureId);
      console.log(
        `📦 [RN-BUNDLE] ✅ HTML loaded, container visible, currentActiveFeature set to: ${featureId}`
      );

      // Also set it in the Angular template container for initial load
      const angularContainer = document.getElementById("react-native-body");
      if (angularContainer) {
        console.log(
          `📦 [RN-BUNDLE] Clearing #react-native-body and appending new container`
        );
        // Clear any existing content (skeleton loader) before appending
        angularContainer.innerHTML = "";
        angularContainer.appendChild(persistentContainer);
        console.log(
          `📦 [RN-BUNDLE] ✅ Container appended to #react-native-body`
        );
      } else {
        console.warn(`📦 [RN-BUNDLE] ⚠️ #react-native-body not found!`);
      }

      // 2. Fetch the JS bundle as text (or arrayBuffer, below)
      const bundleUrl = `${this.baseUrl}bundle.js`;
      let jsContent: string;

      console.log("Getting React Native bundle URL: " + bundleUrl);
      if (isOffline) {
        jsContent = await lastValueFrom(
          this.cacheService
            .getResource(bundleUrl, true)
            .pipe(map((response) => response?.data))
        );
      } else {
        jsContent = await lastValueFrom(
          this.cacheService
            .getResourceAndOnlyCacheIfNative(bundleUrl)
            .pipe(map((response) => response?.data))
        );
      }

      console.log(`React Native JS content: ${JSON.stringify(jsContent)}`);

      // Inject generic navigation capture helper so the shell can read back state
      // from any RN feature without rebuilding the feature.
      this.injectNavigationCaptureScript();
      jsContent = this.patchBundleWithNavigationCapture(jsContent);

      // 3. Create a Blob of type text/javascript
      const blob = new Blob([jsContent], { type: "text/javascript" });

      // 4. Create a temporary URL for that blob
      const scriptUrl = URL.createObjectURL(blob);

      console.log(`React Native script URL: ${JSON.stringify(scriptUrl)}`);

      // 5. Append <script> tag
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = scriptUrl;
      script.setAttribute("data-feature-id", featureId); // For upgrade cleanup
      script.onload = () => {
        // Do your post-load logic
        this.bundleLoaded = true;
        console.log("React Native bundle loaded successfully");

        // Now set the running version based on the actual loaded bundle
        // Fetch the descriptor to get the version that matches this bundle
        this.featureService
          .getFeatureDescriptionByBaseUrl(this.baseUrl)
          .then((descriptor) => {
            if (descriptor?.version) {
              const trimmedBaseUrl = this.baseUrl.trim();
              ReactNativeBundleComponent.runningVersions.set(
                trimmedBaseUrl,
                descriptor.version
              );
              console.log(
                `[FeatureUpgrade] Set running version after bundle load: ${trimmedBaseUrl} -> ${descriptor.version}`
              );
            }
          })
          .catch((err) => {
            console.error(
              "[FeatureUpgrade] Failed to get descriptor for running version:",
              err
            );
          });
      };
      script.onerror = () => {
        console.error("Failed to load the script:", script.src);
        this.networkService.checkInternetConnection();
      };

      console.log(`📦 [RN-BUNDLE] Appending script to document.body`);
      // Run outside Angular's zone: this is where the injected React bundle
      // actually executes and mounts, registering its own event listeners
      // and MutationObservers (e.g. slate-react's). Zone.js captures the
      // "current zone" at listener-registration time and replays every
      // future firing of that listener inside it - if this happens inside
      // Angular's zone, every keystroke in a mounted Slate editor gets
      // routed through zone.js's patched addEventListener/MutationObserver
      // machinery, which is what was causing "Cannot resolve a Slate point
      // from DOM point" errors on every keystroke immediately after a void
      // inline element. Keeping bundle mount/execution outside Angular's
      // zone avoids that interference entirely.
      this.ngZone.runOutsideAngular(() => {
        document.body.appendChild(script);
      });
      console.log(`📦 [RN-BUNDLE] ✅ Script appended, waiting for onload...`);

      // Mark this feature's script as loaded (script now permanently in DOM)
      ReactNativeBundleComponent.loadedFeatures.add(featureId);
      console.log(
        `📦 [RN-BUNDLE] 💾 Module loaded and cached: ${featureId} (script permanently in DOM)`
      );

      // Dispatch initial navigation event after module loads
      // React Native will handle routing to navigationParam or default route
      console.log(
        `📦 [RN-BUNDLE] Dispatching initial angularNavigationChange event`
      );
      const navigationEvent = new CustomEvent("angularNavigationChange", {
        detail: {
          navigationParam: navigationParam || null,
          featureId,
        },
      });
      window.dispatchEvent(navigationEvent);
      console.log(
        "📦 [RN-BUNDLE] ✅ Dispatched initial angularNavigationChange event:",
        {
          navigationParam: navigationParam || "default",
          featureId,
        }
      );
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    } catch (error) {
      console.error("📦 [RN-BUNDLE] ❌ Error loading React app:", error);
      this.networkService.checkInternetConnection();
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    }
  }
}
